import { useEffect, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { logout } from '@/api/auth';
import { config } from '@/config';
import { useSession } from '@/hooks/useSession';
import type { AuthUser } from '@/api/auth';
import { Icon } from './Icon';
import { PAGE_TITLES } from './nav-items';
import { GlobalSearch } from './topbar/GlobalSearch';
import { NotificationsMenu } from './topbar/NotificationsMenu';
import { WhatsNewMenu } from './topbar/WhatsNewMenu';

function initialsFromEmail(email: string): string {
  const parts = email.split('@')[0]?.split(/[._-]/).filter(Boolean) ?? [];
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

/** Инициалы из настоящего имени, если оно подтянулось (см. AuthUser.name), иначе из email. */
function initials(user: AuthUser): string {
  if (!user.name) return initialsFromEmail(user.email);
  const parts = user.name.split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * Верхняя панель: хлебная крошка текущей страницы, общий поиск, уведомления,
 * «что нового» и аккаунт. Каждый из трёх блоков справа — свой компонент в
 * components/topbar/, здесь только раскладка.
 *
 * Данные аккаунта – из общей сессии (см. SessionContext), не отдельным запросом.
 */
export function Topbar() {
  const location = useLocation();
  const { user } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [menuOpen]);

  const onLogout = () => {
    setMenuOpen(false);
    // Сразу на SSO (как переход по кнопке входа на /login), а не на
    // промежуточный /login-стаб – это лишний клик, раз всё равно вести
    // пользователя дальше некуда, кроме как обратно во вход.
    logout()
      .catch(() => {
        // Даже если запрос не дошёл, cookie на клиенте уже не поможет – всё равно ко входу.
      })
      .finally(() => {
        window.location.href = config.loginUrl;
      });
  };

  const title = PAGE_TITLES[location.pathname] ?? 'Компас';

  return (
    <header className="topbar">
      <div className="topbar__crumbs">
        <span className="topbar__crumb-current">{title}</span>
      </div>
      <div className="topbar__right">
        <GlobalSearch />
        <NotificationsMenu />
        <WhatsNewMenu />
        <button
          ref={triggerRef}
          className="topbar__avatar-btn"
          type="button"
          aria-label="Аккаунт"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <span className="topbar__avatar">{initials(user)}</span>
          <Icon name="chevronDown" size={14} />
        </button>
        {menuOpen && (
          <div className="topbar__account-menu" ref={menuRef}>
            {user.name && <div className="topbar__account-name">{user.name}</div>}
            <div className="topbar__account-email">{user.email}</div>
            <Link className="row-menu__item" to="/profile" onClick={() => setMenuOpen(false)}>
              <Icon name="settings" size={15} /> Настройки профиля
            </Link>
            <button
              type="button"
              className="row-menu__item row-menu__item--danger"
              onClick={onLogout}
            >
              <Icon name="chevronRight" size={15} /> Выйти
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
